import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/toast';
import { IProduct, IProductCategory } from '../../shared/interfaces';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import {
  AppstoreOutlined,
  EditOutlined,
  PlusCircleOutlined,
  ProductOutlined,
  RedoOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { Card, Col, Row } from 'antd';
import ProductCreateEdit from './CreateEdit';
import { getProducts } from '../../http/products';
import { getSelectProductCategories } from '../../http';
import { useAuth } from '../../hooks/auth';
import { Screens } from '../../shared/enums/screens.enum';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const Products: React.FC = () => {
  const { addToast } = useToast();
  const { screenFind, permissionFind } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>('');
  const [products, setProducts] = useState<IProduct[]>([]);
  const [categories, setCategories] = useState<IProductCategory[]>([]);
  const [product, setProduct] = useState<IProduct | null>(null);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listProducts = useCallback(async () => {
    setLoading(true);
    setProducts([]);
    try {
      const response = await getProducts(limit, (page - 1) * limit, search);
      setPages(response.count);
      setProducts(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar categorias',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderModalProductCreateEdit = () => {
    if (modalCreate)
      return (
        <ProductCreateEdit
          open={modalCreate}
          close={() => setModalCreate(false)}
          product={product}
          setProducts={setProducts}
          categories={categories}
        />
      );
  };

  const loadCategories = useCallback(async () => {
    await getSelectProductCategories()
      .then((data) => {
        setCategories(data);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description:
            error.response.data.error ?? 'Erro ao listar as categorias.',
        });
      });
  }, []);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    listProducts();
  }, [page, search, limit, listProducts]);

  return (
    <PageLayout>
      <PageIntro
        badge="Estoque"
        icon={<ProductOutlined />}
        title="Produtos"
        description="Cadastre aqui tudo o que a clínica compra, usa ou vende. Use esta tela quando precisar criar ou editar produtos."
      />
      <MenuOptions
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <RedoOutlined />,
            onClick: () => listProducts(),
          },
          permissionFind([PermissionActions.productsCreate])
            ? {
                label: 'Cadastrar',
                key: '2',
                icon: <PlusCircleOutlined />,
                onClick: () => {
                  setProduct(null);
                  setModalCreate(true);
                },
              }
            : null,
          ...(screenFind([Screens.quickSaleView])
            ? [
                {
                  label: 'Venda de Produtos',
                  key: '3',
                  icon: <ProductOutlined />,
                  onClick: () => navigate('/sale'),
                },
              ]
            : []),
          ...(screenFind([Screens.salesView])
            ? [
                {
                  label: 'Acompanhamento de Vendas',
                  key: '4',
                  icon: <ShoppingCartOutlined />,
                  onClick: () => navigate('/sales'),
                },
              ]
            : []),
        ]}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card title="Quando usar esta tela" bordered={false}>
              Cadastre novos itens, ajuste nome, unidade, tipo e preço base de
              venda.
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              title="Quero vender um produto"
              bordered={false}
              extra={<ShoppingCartOutlined />}
            >
              Use a opção Venda de Produtos para registrar a venda e baixar do
              estoque automaticamente.
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card
              title="Quero ver saldos"
              bordered={false}
              extra={<AppstoreOutlined />}
            >
              Use Saldo em Estoque para consultar quantidades disponíveis e
              itens em atenção.
            </Card>
          </Col>
        </Row>
      </PagePanel>
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Cód.',
              dataIndex: 'code',
            },
            {
              title: 'Nome',
              dataIndex: 'name',
            },
            {
              title: 'Unidade',
              dataIndex: 'unity',
            },
            {
              title: 'Tipo',
              dataIndex: 'product_type',
              render: (value: number) =>
                value === 1 ? 'Produto para venda' : 'Material de uso interno',
            },
            {
              title: 'Preço base',
              dataIndex: 'sale_price',
              render: (value: number, record: IProduct) =>
                record.product_type === 1
                  ? new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    }).format(Number(value || 0))
                  : '-',
            },
            {
              title: 'Mínimo',
              dataIndex: 'minimum_inventory',
            },
            {
              title: 'Máximo',
              dataIndex: 'maximum_inventory',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IProduct) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.productsUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            setProduct(cell);
                            setModalCreate(true);
                          },
                        }
                      : null,
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={products}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
      {renderModalProductCreateEdit()}
    </PageLayout>
  );
};

export default Products;
